<template>
  <div class="m-3" style="height: 90vh">
    <div class="grid grid-cols-12 items-center content-center gap-2">
      <el-select
        class="input-label col-span-12 md:col-span-2"
        v-model="selectedValue"
        @change="applyToSelected"
      >
        <template v-if="$can('read', '/accounting/accounts')">
          <el-option
            key="export"
            :label="$t('views.common.listView.selectedRows.export')"
            value="export"
          >
          </el-option>
        </template>
        <template v-if="$can('delete', '/accounting/accounts')">
          <el-option
            key="delete"
            :label="$t('views.common.listView.selectedRows.delete')"
            value="delete"
          >
          </el-option>
        </template>
      </el-select>

      <el-input
        :placeholder="$t('views.common.listView.searchRows')"
        class="w-full col-span-12 md:col-span-4"
        :prefix-icon="Search"
        v-model="searchTerm"
      />

      <el-select
        class="input-label col-span-12 md:col-span-2"
        v-model="selectedLocation"
        clearable
        :placeholder="t('selectLocation')"
        value-key="id"
        @change="onLocationFilterChange"
        :disabled="!canViewAllLocations"
      >
        <el-option
          v-for="option in locations"
          :key="option.id"
          :label="$ctx.locale === 'en' ? option.enName : option.arName"
          :value="option"
        ></el-option>
      </el-select>

      <div class="w-full col-span-12 md:col-span-2">
        <el-button
          :type="showPeopleCompanies ? 'primary' : 'default'"
          @click="togglePeopleCompaniesFilter"
          class="w-full"
          size="large"
        >
          {{ t('peopleCompanies') }}
        </el-button>
      </div>

      <div class="w-full col-span-12 md:col-span-2">
        <template v-if="$can('create', '/accounting/accounts')">
          <horizontal-button
            :title="$t('views.accounting.accounts.newAccount')"
            @click.prevent="$router.push($Route.ACCOUNTING_ACCOUNTS_NEW_ACCOUNT_TEMPLATE)"
          />
        </template>
      </div>
    </div>

    <el-tree-v2
      ref="accountsTree"
      :data="filteredTreeData"
      :props="{ value: 'value', label: 'label', children: 'children' }"
      show-checkbox
      :default-expanded-keys="['assets', 'liabilities', 'equity', 'revenue', 'expenses']"
      :height="600"
      :item-size="38"
      class="accounts-tree mt-4"
      :class="{ 'rtl-tree': $ctx.getDir() === 'rtl' }"
    >
      <template #default="{ node, data }">
        <div class="custom-tree-node" :class="{ 'is-category': data.isCategory }">
          <span class="account-info">
            <span class="account-code" v-if="data.code">{{ data.code }}</span>
            <span class="account-name">{{ node.label }}</span>
            <span class="account-location" v-if="locationName(data)">{{ locationName(data) }}</span>
          </span>

          <div class="node-right" v-if="data.value && !data.isCategory">
            <span
              v-if="data.account && data.account.balance"
              class="balance-badge"
              :class="data.account.balance === 'credit' ? 'is-credit' : 'is-debit'"
            >
              {{ data.account.balance === 'credit' ? t('credit') : t('debit') }}
            </span>

            <div class="action-buttons">
              <el-button
                type="primary"
                link
                size="small"
                @click.stop="onView(data.value)"
              >
                {{ $t('views.common.listView.view') }}
              </el-button>
              <el-button
                type="primary"
                link
                size="small"
                @click.stop="onEdit(data.value)"
              >
                {{ $t('views.common.listView.edit') }}
              </el-button>
              <el-button
                type="primary"
                link
                size="small"
                @click.stop="onLog(data.value)"
              >
                {{ t('log') }}
              </el-button>
            </div>
          </div>
        </div>
      </template>
    </el-tree-v2>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

// components
import HorizontalButton from '@/components/HorizontalButton.vue'

import { Account, accountI18nMessages, AccountCategory } from '@/models/accounting/Account'
import Location from '@/models/company/Location'
import { useI18n } from 'vue-i18n'
import { Search } from '@element-plus/icons-vue'
import { collectTreeKeys, filterAccountsTree, type AccountTreeNode } from './accountTreeFilter'

interface TreeNode extends AccountTreeNode {
  account?: Account
  children?: TreeNode[]
}

export default defineComponent({
  name: 'accounts',

  setup() {
    const { t } = useI18n({
      messages: {
        en: {
          ...accountI18nMessages.en,
          name: 'Name',
          description: 'Description',
          location: 'Location',
          selectLocation: 'Select Location',
          log: 'Log',
          peopleCompanies: 'People/Companies',
          debit: 'Debit',
          credit: 'Credit',
        },
        ar: {
          ...accountI18nMessages.ar,
          name: 'الاسم',
          description: 'الوصف',
          location: 'الموقع',
          selectLocation: 'اختر الموقع',
          log: 'السجل',
          peopleCompanies: 'الأفراد/الشركات',
          debit: 'مدين',
          credit: 'دائن',
        }
      }
    })

    return {
      t,
      Search
    }
  },

  data() {
    const accountsTreeData: TreeNode[] = []
    const allAccounts: Account[] = []
    const locations: Location[] = []

    return {
      accountsTreeData,
      allAccounts,
      locations,
      selectedLocation: null as Location | null,
      searchTerm: '',
      selectedValue: '',
      canViewAllLocations: false,
      showPeopleCompanies: false
    }
  },

  components: {
    HorizontalButton
  },

  async mounted() {
    // Check if user can view all locations
    this.canViewAllLocations = this.$can('read', '/accounting/accounts/view-all-locations')

    await this.loadLocations()

    // Default to no location selected for users who can view all locations.
    // Restricted users (the filter is disabled for them) stay scoped to their
    // current location so they don't see other locations' accounts.
    if (!this.canViewAllLocations && this.$ctx.currentLocation && this.locations.length > 0) {
      const currentLocation = this.locations.find(loc => loc.id === this.$ctx.currentLocation.id)
      if (currentLocation) {
        this.selectedLocation = currentLocation
      }
    }

    await this.loadAccounts()
  },

  computed: {
    filteredTreeData() {
      return filterAccountsTree(this.accountsTreeData, this.searchTerm)
    }
  },

  watch: {
    filteredTreeData: {
      handler(nodes: TreeNode[]) {
        this.$nextTick(() => {
          const tree = this.$refs.accountsTree as { setExpandedKeys?: (keys: string[]) => void } | undefined
          if (!tree?.setExpandedKeys) return

          if (!this.searchTerm) {
            tree.setExpandedKeys(['assets', 'liabilities', 'equity', 'revenue', 'expenses'])
            return
          }

          tree.setExpandedKeys(collectTreeKeys(nodes))
        })
      }
    }
  },

  methods: {
    async loadLocations() {
      try {
        if (this.canViewAllLocations) {
          // User can view all locations
          const data = await this.$http.get<Location[]>(Location.ENDPOINT)
          this.locations = data.map((location) => Location.from(location))
        } else {
          // User can only see current location
          if (this.$ctx.currentLocation) {
            this.locations = [Location.from(this.$ctx.currentLocation)]
          }
        }
      } catch (error: any) {
        this.$alertModal.showDanger({
          title: error.title || 'Error',
          body: error.body || 'Failed to load locations'
        })
      }
    },

    async loadAccounts() {
      try {
        let endpoint = Account.ENDPOINT
        const params = new URLSearchParams()

        // Add location filter if selected
        if (this.selectedLocation) {
          params.append('location_id', this.selectedLocation.id)
        }

        // Add people/companies filter if enabled
        if (this.showPeopleCompanies) {
          params.append('show_people_companies', 'true')
        }

        if (params.toString()) {
          endpoint = `${endpoint}?${params.toString()}`
        }

        const data = await this.$http.get<Account[]>(endpoint)
        const accounts = data.map((account) => Account.from(account))

        // Fetch locations for accounts that have location references
        const locationIds = accounts
          .filter(account => account.referenceableType === 'location.Location' && account.referenceableID)
          .map(account => account.referenceableID!)

        if (locationIds.length > 0) {
          const locations = await this.$http.get<Location[]>(Location.ENDPOINT)
          const locationMap = new Map(locations.map(loc => [loc.id, loc]))

          // Add location data to accounts
          accounts.forEach(account => {
            if (account.referenceableType === 'location.Location' && account.referenceableID) {
              const location = locationMap.get(account.referenceableID)
              if (location) {
                account.location = Location.from(location)
              }
            }
          })
        }

        this.allAccounts = accounts
        this.buildAccountsTree()
      } catch (error: any) {
        this.$alertModal.showDanger({
          title: error.title || 'Error',
          body: error.body || 'Failed to load accounts'
        })
      }
    },

    onLocationFilterChange() {
      this.loadAccounts()
    },

    togglePeopleCompaniesFilter() {
      this.showPeopleCompanies = !this.showPeopleCompanies
      this.loadAccounts()
    },

    buildAccountsTree() {
      const locale = this.$ctx.locale as 'en' | 'ar'
      const accountsByCategory: Record<string, Account[]> = {
        assets: [],
        liabilities: [],
        equity: [],
        revenue: [],
        expenses: []
      }

      // Group accounts by category
      this.allAccounts.forEach(account => {
        if (account.accountCategory) {
          accountsByCategory[account.accountCategory].push(account)
        }
      })

      // Category labels
      const categoryLabels: Record<string, { en: string; ar: string }> = {
        assets: { en: 'Assets', ar: 'الأصول' },
        liabilities: { en: 'Liabilities', ar: 'الإلتزمات' },
        equity: { en: 'Equity', ar: 'حقوق الملكية' },
        revenue: { en: 'Revenue', ar: 'الإيرادات' },
        expenses: { en: 'Expenses', ar: 'المصروفات' }
      }

      // Build tree structure
      this.accountsTreeData = Object.entries(accountsByCategory).map(([category, accounts]) => {
        const categoryNode: TreeNode = {
          value: category,
          label: categoryLabels[category][locale],
          isCategory: true,
          children: this.buildAccountHierarchy(accounts, locale)
        }
        return categoryNode
      }).filter(node => node.children && node.children.length > 0)
    },

    buildAccountHierarchy(accounts: Account[], locale: 'en' | 'ar'): TreeNode[] {
      const accountMap = new Map<string, TreeNode>()
      const rootAccounts: TreeNode[] = []

      // Create nodes for all accounts
      accounts.forEach(account => {
        const name = locale === 'en' ? account.enName : account.arName
        const node: TreeNode = {
          value: account.id!,
          label: name,
          code: account.code?.toString(),
          account: account,
          children: []
        }
        accountMap.set(account.id!, node)
      })

      // Build parent-child relationships
      accounts.forEach(account => {
        const node = accountMap.get(account.id!)
        if (node) {
          if (account.parentAccountID && accountMap.has(account.parentAccountID)) {
            const parentNode = accountMap.get(account.parentAccountID)
            if (parentNode) {
              if (!parentNode.children) {
                parentNode.children = []
              }
              parentNode.children.push(node)
            }
          } else {
            rootAccounts.push(node)
          }
        }
      })

      // Remove empty children arrays
      const cleanEmptyChildren = (node: TreeNode) => {
        if (node.children?.length === 0) {
          delete node.children
        } else if (node.children) {
          node.children.forEach(cleanEmptyChildren)
        }
      }
      rootAccounts.forEach(cleanEmptyChildren)

      return rootAccounts
    },

    async delete(accounts: Account[]) {
      if (confirm(this.$t('views.common.listView.selectedRows.deleteConfirm'))) {
        try {
          await this.$http.delete(Account.ENDPOINT, { data: accounts })
          await this.loadAccounts()
          this.$alertModal.showSuccess({
            title: this.$t('views.common.listView.selectedRows.deleteSuccess')
          })
        } catch (error: any) {
          this.$alertModal.showDanger({
            title: error.title || 'Error',
            body: error.body || 'Failed to delete accounts'
          })
        }
      }
    },

    async applyToSelected() {
      if (this.selectedValue === 'delete' || this.selectedValue === 'export') {
        const tree = this.$refs.accountsTree as any
        const checkedNodes = tree?.getCheckedNodes()
        const selected = checkedNodes?.filter((node: TreeNode) => !node.isCategory && node.account)
          .map((node: TreeNode) => node.account) as Account[]

        if (selected?.length) {
          if (this.selectedValue === 'delete') {
            await this.delete(selected)
          } else {
            // Export to CSV
            this.exportToCSV(selected)
            this.$alertModal.showSuccess({
              title: this.$t('views.common.listView.selectedRows.exportSuccess')
            })
          }

          // Deselect all
          tree?.setCheckedKeys([])
        }
      }

      this.selectedValue = ''
    },

    exportToCSV(accounts: Account[]) {
      const locale = this.$ctx.locale as 'en' | 'ar'
      const headers = ['Code', 'Name', 'Description', 'Category', 'Parent Account']
      const rows = accounts.map(account => [
        account.code?.toString() || '',
        locale === 'en' ? account.enName : account.arName,
        locale === 'en' ? (account.enDescription || '') : (account.arDescription || ''),
        account.accountCategory || '',
        account.parentAccount ? (locale === 'en' ? account.parentAccount.enName : account.parentAccount.arName) : ''
      ])

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `accounts_${new Date().getTime()}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    locationName(data: TreeNode): string {
      const location = data.account?.location
      if (!location) return ''
      return this.$ctx.locale === 'en' ? location.enName : location.arName
    },

    onView(accountId: string) {
      this.$router.push(this.$Route.ACCOUNTING_ACCOUNTS_VIEW.replace(':id', accountId))
    },

    onEdit(accountId: string) {
      this.$router.push(this.$Route.ACCOUNTING_ACCOUNTS_ACCOUNT.replace(':id', accountId))
    },

    onLog(accountId: string) {
      this.$router.push(this.$Route.ACCOUNTING_ACCOUNTS_LOG.replace(':id', accountId))
    }
  }
})
</script>

<style scoped>
.accounts-tree {
  margin-top: 1rem;
  max-height: 75vh;
  overflow-y: auto;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 1rem;
  background-color: #ffffff;
}

.custom-tree-node {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.2rem 0.5rem 0.2rem 0;
  font-size: 13.5px;
}

.account-info {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex: 1;
}

.account-code {
  font-weight: 600;
  color: #606266;
  background-color: #f4f4f5;
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
  font-size: 12px;
  min-width: 48px;
  text-align: center;
}

.account-name {
  color: #303133;
  font-weight: 500;
}

/* Category headers (Assets, Liabilities, …) */
.custom-tree-node.is-category .account-name {
  font-weight: 700;
  font-size: 15px;
  color: #1f2937;
}

/* Location chip */
.account-location {
  display: inline-flex;
  align-items: center;
  padding: 0.1rem 0.5rem;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 500;
  color: hsl(222.2 47.4% 35%);
  background-color: #eef2f8;
  white-space: nowrap;
}

/* Right-hand side: normal-balance badge + actions */
.node-right {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  margin-left: auto;
}

.balance-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 56px;
  padding: 0.1rem 0.6rem;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
}

.balance-badge.is-debit {
  color: hsl(222.2 47.4% 30%);
  background-color: #e8eefb;
}

.balance-badge.is-credit {
  color: #2e7d32;
  background-color: #e8f5ea;
}

.action-buttons {
  display: flex;
  gap: 0.6rem;
}

.action-buttons :deep(.el-button) {
  font-size: 13px;
}

/* Dark mode support for tree */
:deep(.el-tree-node__content) {
  height: auto;
  min-height: 34px;
  padding: 0.1rem 0;
  border-bottom: 1px solid #eef0f3;
}

:deep(.el-tree-node__content:hover) {
  background-color: #f5f7fa;
}

:deep(.el-tree-node__label) {
  flex: 1;
}

/* Category nodes styling */
:deep(.el-tree-node.is-expanded > .el-tree-node__children) {
  padding-left: 1rem;
}

/* Checkbox styling */
:deep(.el-checkbox) {
  margin-right: 0.5rem;
}

/* RTL support */
.rtl-tree :deep(.el-tree-node__content) {
  transform: scaleX(-1);
}

.rtl-tree :deep(.el-tree-node__content) > .el-checkbox,
.rtl-tree :deep(.custom-tree-node) {
  transform: scaleX(-1);
}

/* scaleX(-1) on the row plus rtlcss rotate(-90deg) makes the expanded caret
   point up. Force the LTR down-facing rotation. */
.rtl-tree :deep(.el-tree-node__expand-icon.expanded) {
  transform: rotate(90deg);
}

/* Reverse the custom node: account info on the right (next to the checkbox),
   balance badge + action buttons on the left. */
.rtl-tree .custom-tree-node {
  flex-direction: row-reverse;
  padding: 0.2rem 0 0.2rem 0.5rem;
}

.rtl-tree .account-info {
  flex-direction: row-reverse;
  text-align: right;
}

.rtl-tree .account-name {
  text-align: right;
}

.rtl-tree .node-right {
  flex-direction: row-reverse;
  margin-left: 0;
  margin-right: auto;
}
</style>
